import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import type { ReactNode } from 'react'

import {
  THEME_STORAGE_KEY,
  ThemeContext,
  type ResolvedTheme,
  type ThemeContextValue,
  type ThemePreference,
} from '@pushcaster/ui/theme-context'

/*
 * DESIGN_SYSTEM.md §3: `prefers-color-scheme` is honoured, an explicit choice
 * overrides and persists, and dark is the resolution when the OS expresses no
 * preference.
 *
 * This provider owns the same ladder `public/theme-init.js` runs before first
 * paint. The duplication is real and is the price of not flashing: that script
 * has to execute before any module loads, so it cannot import this one. What
 * keeps them honest is that neither knows a colour — both only set an
 * attribute, and every value comes from tokens.css.
 */

const LIGHT = '(prefers-color-scheme: light)'
const DARK = '(prefers-color-scheme: dark)'

function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    /* Private mode, or storage disabled. The OS preference still applies. */
    return 'system'
  }
}

function subscribeToOperatingSystem(onChange: () => void): () => void {
  const queries = [window.matchMedia(LIGHT), window.matchMedia(DARK)]
  for (const query of queries) query.addEventListener('change', onChange)
  return () => {
    for (const query of queries) query.removeEventListener('change', onChange)
  }
}

/**
 * The OS's answer, with the case the CSS cannot express.
 *
 * A browser that expresses no preference matches neither query, and the rule is
 * that dark wins there. Asking only for `dark` — the usual one-liner — would
 * read "no preference" as light, which is the opposite of what was specified.
 */
function readOperatingSystemTheme(): ResolvedTheme {
  if (window.matchMedia(DARK).matches) return 'dark'
  if (window.matchMedia(LIGHT).matches) return 'light'
  return 'dark'
}

/**
 * Mirrors the resolved theme onto `<html>`.
 *
 * The attribute is set only when the stylesheet would resolve differently on
 * its own, so a reader who has never touched the toggle keeps following their
 * OS when it flips at sunset. Pinning it unconditionally would freeze them at
 * whatever it was when the tab opened — which looks like the toggle working and
 * is in fact the toggle being stuck.
 */
function applyTheme(preference: ThemePreference, resolved: ResolvedTheme): void {
  const root = document.documentElement

  /*
   * Suppress transitions across the flip.
   *
   * A theme change is a repaint, not a state change, and DESIGN_SYSTEM.md §10
   * is explicit that motion here either communicates a state change or does not
   * exist — cross-fading forty colours over 120 ms is the ambient animation the
   * system forbids. It is also incorrect: every element carrying
   * `transition-colors` for its *hover* state inherits a colour that changed
   * because a custom property under it changed, and Chrome strands a number of
   * them on the outgoing value rather than finishing the transition. Found by
   * running axe after a theme switch and being told the light theme was drawing
   * dark text on a light ground — which it was.
   *
   * The forced style read between setting the attribute and clearing the guard
   * is what makes this work: it commits the new colours while transitions are
   * still off, so there is nothing left to animate when the guard lifts.
   */
  root.setAttribute('data-theme-switching', '')

  if (preference === 'system' && readOperatingSystemTheme() === resolved) {
    const expressesPreference =
      window.matchMedia(LIGHT).matches || window.matchMedia(DARK).matches
    if (expressesPreference) {
      root.removeAttribute('data-theme')
    } else {
      root.setAttribute('data-theme', resolved)
    }
  } else {
    root.setAttribute('data-theme', resolved)
  }

  void window.getComputedStyle(root).backgroundColor
  root.removeAttribute('data-theme-switching')
}

/**
 * `<meta name="theme-color">` follows the page ground, read from the *computed*
 * `--color-bg-app` rather than from a constant here. tokens.css stays the only
 * place either colour is written down; a pair of hexes in this file would be
 * the copy nobody remembers to update.
 */
function applyThemeColor(): void {
  const ground = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-bg-app')
    .trim()
  if (ground === '') return

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta === null) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = ground
}

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference)

  const operatingSystemTheme = useSyncExternalStore(
    subscribeToOperatingSystem,
    readOperatingSystemTheme,
    () => 'dark' as const,
  )

  const resolved: ResolvedTheme = preference === 'system' ? operatingSystemTheme : preference

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      if (next === 'system') {
        window.localStorage.removeItem(THEME_STORAGE_KEY)
      } else {
        window.localStorage.setItem(THEME_STORAGE_KEY, next)
      }
    } catch {
      /* Storage refused. The choice still applies to this session. */
    }
    setPreferenceState(next)
  }, [])

  /*
   * The attribute goes on in a *layout* effect, and the distinction is not
   * cosmetic.
   *
   * Passive effects run after paint, so applying the attribute in one shows a
   * frame of the outgoing theme every time the toggle is used. Worse, React
   * runs child effects before parent effects — so any descendant reading
   * `getComputedStyle` in a passive effect would read the *previous* theme's
   * values and be a frame stale forever, not just once. All layout effects
   * complete before any passive effect, which puts the attribute in place
   * before both the paint and every reader downstream. The token gallery is
   * exactly such a reader, and it is how this was found.
   */
  useLayoutEffect(() => {
    applyTheme(preference, resolved)
  }, [preference, resolved])

  /* The browser chrome, on the other hand, nobody sees until the next frame,
     and it has to read the colour the attribute above just decided. */
  useEffect(() => {
    applyThemeColor()
  }, [resolved])

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
